import PropTypes from "prop-types";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import TaskCard from "./TaskCard";
import { cn } from "@/lib/utils";

function TaskColumn({
  status,
  label,
  tasks,
  showProjectBadge = false,
  projectsById = {},
  onCardClick = undefined,
  droppableId,
  footer = null,
}) {
  // The column itself is a droppable target so that drags into an empty
  // column still register — SortableContext alone does not cover this
  // because there are no card-level drop targets inside an empty column.
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });

  const taskIds = tasks.map((task) => task.id);

  return (
    <div
      ref={setNodeRef}
      data-testid={`column-${status}`}
      className={cn(
        "task-column min-w-60 flex-1 rounded-lg p-2 transition-colors",
        // Dark mode lifts the column to the card surface so the board reads
        // as paper like the other sections (it is mostly bare page
        // background otherwise); cards sit one step lighter again (#36).
        isOver ? "bg-muted dark:bg-secondary" : "bg-muted/40 dark:bg-card",
      )}
    >
      <h2 className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {label} <span className="ml-1">({tasks.length})</span>
      </h2>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        {tasks.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No tasks</div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              showProjectBadge={showProjectBadge}
              projectsById={projectsById}
              onClick={onCardClick}
            />
          ))
        )}
      </SortableContext>
      {footer}
    </div>
  );
}

TaskColumn.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tasks: PropTypes.array.isRequired,
  showProjectBadge: PropTypes.bool,
  projectsById: PropTypes.object,
  onCardClick: PropTypes.func,
  droppableId: PropTypes.string.isRequired,
  footer: PropTypes.node,
};

export default TaskColumn;
